import { Add } from '@mui/icons-material';
import { Box, Button, Typography } from '@mui/material';
import * as React from 'react';
import { FormattedMessage } from 'react-intl';
import { CSVReader } from 'react-papaparse';
import { IconCSV } from '../../../../svg';
import LoadingIcon from '../../../common/component/LoadingIcon';
import NoDataBox from '../../../common/component/NoDataBox';
import { some } from '../../../common/constants';
import useGeneralHook from '../../../common/hook/useGeneralHook';
import { setLoading } from '../../../common/redux/commonReducer';
import { sendToBackend } from './CSVDialog/csv';
import CSVPreviewDialog from './CSVDialog/CSVPreviewDialog';
import ExamTable from './ExamTable';
import SearchExamPrice from './SearchExamPrice';
import { Service } from 'modules/schema';
import { filter } from 'lodash';

interface Props {
  data: some[];
  loading?: boolean;
  setFormData: React.Dispatch<React.SetStateAction<some>>;
  setFormDataGroup: (value?: some) => void;
  onDelete: (value: some) => void;
  onDeleteGroup: (value: some) => void;
  onDeleteServices: (services: Service[]) => void;
  searchParams: some;
  setSearchParams: React.Dispatch<React.SetStateAction<some>>;
  isInsurance: boolean;
  categoryId: number;
  revalidate: () => void;
}

const ExamPriceList: React.FunctionComponent<Props> = (props) => {
  const {
    data,
    searchParams,
    isInsurance,
    loading,
    setFormData,
    setFormDataGroup,
    onDelete,
    onDeleteGroup,
    onDeleteServices,
    setSearchParams,
    categoryId,
    revalidate,
  } = props;
  const { dispatch, intl, appState, openSnackbar } = useGeneralHook();
  const buttonRef = React.useRef<any>(null);
  const [dataCSV, setDataCSV] = React.useState<some[] | undefined>(undefined);

  const handleOnDrop = (data) => {
    setDataCSV(data);
  };

  const handleOnRemoveFile = (data) => {
    setDataCSV(undefined);
  };

  const handleOpenDialog = (e) => {
    // Note that the ref is set async, so it might be null at some point
    if (buttonRef.current) {
      buttonRef.current?.open(e);
    }
  };

  if (!loading && data?.length === 0 && !searchParams?.name) {
    return (
      <Box padding={2}>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          height="50vh"
          overflow="auto"
        >
          <Typography variant="h5" color="primary">
            <FormattedMessage id="exam.empty" />
          </Typography>

          <Box padding={2}>
            <Typography variant="body1">
              <FormattedMessage id="addNewList" />
            </Typography>
          </Box>
          <Box display="flex">
            <CSVReader
              ref={buttonRef}
              onRemoveFile={handleOnRemoveFile}
              onDrop={handleOnDrop}
              noClick
              noDrag
              noProgressBar
              config={{
                encoding: 'utf-8',
              }}
            >
              {() => (
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<IconCSV />}
                  style={{ marginRight: 16 }}
                  onClick={handleOpenDialog}
                >
                  <FormattedMessage id="importCSV" />
                </Button>
              )}
            </CSVReader>
            <Button variant="contained" color="primary" startIcon={<Add />} onClick={() => setFormDataGroup({})}>
              <FormattedMessage id="add" />
            </Button>
          </Box>
        </Box>
        <CSVPreviewDialog
          open={!!dataCSV}
          dataCSV={dataCSV}
          onClose={() => setDataCSV(undefined)}
          isInsurance={isInsurance}
          onSubmit={async (submittedCSVData) => {
            if (submittedCSVData.length < 1) {
              return;
            }
            if (filter(submittedCSVData, (obj) => obj?.name.length > 255).length !== 0) {
              openSnackbar({
                message: intl.formatMessage({ id: 'validator.length.name.import.csv' }),
                type: 'error',
              });
              return;
            }
            dispatch(setLoading(true));
            await sendToBackend(submittedCSVData, dispatch, appState, categoryId)
              .then(() => {
                revalidate();
                setDataCSV(undefined);
              })
              .finally(() => {
                dispatch(setLoading(false));
              });
          }}
        />
      </Box>
    );
  }

  const dataChecked = data?.filter((d) => !(searchParams?.name && (!d.items || d.items?.length <= 0)));
  return (
    <>
      <Box padding={2} paddingBottom={1}>
        <SearchExamPrice
          onAddNew={() => setFormDataGroup({})}
          onSubmit={(value) => {
            setSearchParams(value);
          }}
          formData={searchParams}
        />
      </Box>
      {loading ? (
        <LoadingIcon />
      ) : (
        <Box padding={2} paddingTop={0} overflow="auto">
          {dataChecked?.length > 0 ? (
            dataChecked.map((value: some) => {
              return (
                <ExamTable
                  key={value.id}
                  setFormData={setFormData}
                  setFormDataGroup={setFormDataGroup}
                  onDelete={onDelete}
                  onDeleteGroup={onDeleteGroup}
                  onDeleteServices={onDeleteServices}
                  data={value}
                />
              );
            })
          ) : (
            <NoDataBox />
          )}
        </Box>
      )}
    </>
  );
};

export default ExamPriceList;
